import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  EXPORT_LINE_COLUMNS,
  type ExportLineColumn,
} from '@octave/contracts';
import { Check, Download, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

const exportFormSchema = z.object({
  sites: z.array(z.string()).min(1, {
    message: 'Sélectionnez au moins un centre de facturation',
  }),
  columns: z.array(z.enum(EXPORT_LINE_COLUMNS)).min(1, {
    message: 'Sélectionnez au moins une colonne',
  }),
  format: z.enum(['xlsx', 'csv']),
  delimiter: z.string().optional(),
  includeInactive: z.boolean().default(false),
});

export type ExportFormValues = z.infer<typeof exportFormSchema>;

export function ExportDialog({
  isOpen,
  onOpenChange,
  sites,
  availableColumns,
  onExport,
  isExporting,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  sites: Array<{ id: string; name: string }>;
  availableColumns: Array<{ id: ExportLineColumn; label: string }>;
  onExport: (data: ExportFormValues) => void;
  isExporting: boolean;
}) {
  const form = useForm<ExportFormValues>({
    resolver: zodResolver(exportFormSchema),
    defaultValues: {
      sites: [],
      columns: availableColumns.map((col) => col.id),
      format: 'xlsx',
      delimiter: ';',
      includeInactive: true,
    },
  });

  function onSubmit(data: ExportFormValues) {
    onExport(data);
    form.reset();
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="hidden sm:inline-flex gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50"
        >
          <Download className="h-4 w-4" />
          Exporter le parc
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-0 shrink-0">
          <DialogTitle className="text-lg font-bold text-slate-900">
            Configuration de l'export
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Sélectionnez les centres, colonnes et options d'export
          </p>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col flex-1 overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
              <Tabs defaultValue="sites" className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-slate-100 p-1 rounded-xl">
                  <TabsTrigger
                    value="sites"
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                  >
                    Centres de facturation
                  </TabsTrigger>
                  <TabsTrigger
                    value="columns"
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                  >
                    Colonnes
                  </TabsTrigger>
                  <TabsTrigger
                    value="options"
                    className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all duration-200 text-sm"
                  >
                    Options
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="sites" className="space-y-3 mt-4">
                  <FormField
                    control={form.control}
                    name="sites"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-200">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={
                                field.value.length === sites.length
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  field.onChange(
                                    sites.map((site) => site.id)
                                  );
                                } else {
                                  field.onChange([]);
                                }
                              }}
                            />
                            <FormLabel className="text-sm font-medium text-slate-700">
                              Tous les centres de facturation (
                              {sites.length})
                            </FormLabel>
                          </div>
                          {field.value.length > 0 && (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                              {field.value.length} sélectionné{field.value.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        <div className="max-h-[200px] overflow-y-auto rounded-xl border border-slate-200 bg-white">
                          {sites.map((site) => (
                            <button
                              type="button"
                              key={site.id}
                              onClick={() => {
                                if (field.value.includes(site.id)) {
                                  field.onChange(
                                    field.value.filter(
                                      (id) => id !== site.id
                                    )
                                  );
                                } else {
                                  field.onChange([
                                    ...field.value,
                                    site.id,
                                  ]);
                                }
                              }}
                              className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                field.value.includes(site.id)
                                  ? 'bg-emerald-50'
                                  : 'hover:bg-slate-50'
                              }`}
                            >
                              <div
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-all ${
                                  field.value.includes(site.id)
                                    ? 'border-emerald-500 bg-emerald-500'
                                    : 'border-slate-300 bg-white'
                                }`}
                              >
                                {field.value.includes(site.id) && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                              <span
                                className={`text-sm truncate ${
                                  field.value.includes(site.id)
                                    ? 'text-emerald-900 font-medium'
                                    : 'text-slate-700'
                                }`}
                              >
                                {site.name}
                              </span>
                            </button>
                          ))}
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="columns" className="space-y-3 mt-4">
                  <FormField
                    control={form.control}
                    name="columns"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-200">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={
                                field.value.length ===
                                availableColumns.length
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  field.onChange(
                                    availableColumns.map((col) => col.id)
                                  );
                                } else {
                                  field.onChange([]);
                                }
                              }}
                            />
                            <FormLabel className="text-sm font-medium text-slate-700">
                              Toutes les colonnes (
                              {availableColumns.length})
                            </FormLabel>
                          </div>
                          {field.value.length > 0 && (
                            <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5">
                              {field.value.length} sélectionnée{field.value.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>

                        <div className="max-h-[300px] overflow-y-auto rounded-xl border border-slate-200 bg-white">
                          <div className="grid grid-cols-2 gap-px bg-slate-100">
                            {availableColumns.map((column) => (
                              <button
                                type="button"
                                key={column.id}
                                onClick={() => {
                                  if (field.value.includes(column.id || '')) {
                                    field.onChange(
                                      field.value.filter(
                                        (id) => id !== column.id
                                      )
                                    );
                                  } else {
                                    field.onChange([
                                      ...field.value,
                                      column.id,
                                    ]);
                                  }
                                }}
                                className={`flex items-center gap-3 px-3 py-2.5 text-left transition-colors bg-white ${
                                  field.value.includes(column.id || '')
                                    ? 'bg-emerald-50'
                                    : 'hover:bg-slate-50'
                                }`}
                              >
                                <div
                                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-all ${
                                    field.value.includes(column.id || '')
                                      ? 'border-emerald-500 bg-emerald-500'
                                      : 'border-slate-300 bg-white'
                                  }`}
                                >
                                  {field.value.includes(column.id || '') && (
                                    <Check className="h-3 w-3 text-white" />
                                  )}
                                </div>
                                <span
                                  className={`text-sm truncate ${
                                    field.value.includes(column.id || '')
                                      ? 'text-emerald-900 font-medium'
                                      : 'text-slate-700'
                                  }`}
                                >
                                  {column.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </TabsContent>

                <TabsContent value="options" className="space-y-4 mt-4">
                  <FormField
                    control={form.control}
                    name="format"
                    render={({ field }) => (
                      <FormItem className="space-y-1.5">
                        <FormLabel className="text-xs font-medium text-slate-500">
                          Format d'export
                        </FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="bg-white border-slate-200 text-sm">
                              <SelectValue placeholder="Choisir le format" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="xlsx">
                              Excel (XLSX)
                            </SelectItem>
                            <SelectItem value="csv">CSV</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {form.watch('format') === 'csv' && (
                    <FormField
                      control={form.control}
                      name="delimiter"
                      render={({ field }) => (
                        <FormItem className="space-y-1.5">
                          <FormLabel className="text-xs font-medium text-slate-500">
                            Délimiteur CSV
                          </FormLabel>
                          <FormControl>
                            <Input
                              {...field}
                              maxLength={1}
                              className="w-20 bg-white border-slate-200 text-sm"
                            />
                          </FormControl>
                          <p className="text-xs text-slate-400">
                            Caractère utilisé pour séparer les colonnes
                          </p>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  <FormField
                    control={form.control}
                    name="includeInactive"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between space-y-0 p-4 bg-slate-50/50 rounded-xl border border-slate-200">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium text-slate-700">
                            Inclure les lignes inactives
                          </FormLabel>
                          <p className="text-xs text-slate-400">
                            Exporter aussi les lignes résiliées
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </TabsContent>
              </Tabs>
            </div>

            <div className="flex justify-between px-6 pb-6 pt-3 shrink-0 border-t border-slate-100 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset()}
                className="rounded-full border-slate-200 text-slate-600"
              >
                Réinitialiser
              </Button>
              <Button
                type="submit"
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all flex items-center gap-2"
                disabled={
                  isExporting ||
                  form.watch('sites').length === 0 ||
                  form.watch('columns').length === 0
                }
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Exporter ({form.watch('sites').length} centre de
                facturation
                {form.watch('sites').length > 1 ? 's' : ''})
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
